/**
 * Gallery ExpandedView — inline wrapper around the shared LabelDetail controller.
 *
 * Hosts LabelDetail inside a `.label-detail.label-detail--inline` container. Manages open/close visibility, prev/next
 * paging, thumbnail highlighting, card transparency, and syncing validation results back onto the small cards.
 *
 * Construct instances via the `static async create()` factory, which initializes LabelDetail before resolving.
 */

import i18next from 'i18next';
import { LabelDetail } from '../../common/label-detail/LabelDetail.js';
import { PopupPanoManager } from '../../common/label-detail/PopupPanoManager.js';
import { closeReasonPopover } from '../../common/ReasonPopover.js';
/** @typedef {import('../cards/Card.js').Card} Card */
/** @typedef {import('../cards/CardContainer.js').CardContainer} CardContainer */
/** @typedef {import('../data/Tracker.js').Tracker} Tracker */

/**
 * What the imagery viewer is built with, as the page carries it.
 * @typedef {object} ViewerParams
 * @property {typeof import('../../common/pano-viewer/PanoViewer.js').PanoViewer} panoViewerType - The viewer class.
 * @property {string} viewerAccessToken - An access token that authorizes image requests for the pano viewer.
 * @property {?string} currUsername - The viewer's username when signed in to a real account, else null.
 */

export class ExpandedView {
  static #unselectedCardClassName = 'expanded-view-background-card';

  #root;
  #viewerParams;
  #cardContainer;
  #tracker;

  /**
   * @param {HTMLElement} root - The `.gallery-expanded-view` container element.
   * @param {ViewerParams} viewerParams - What the imagery viewer is built with.
   * @param {CardContainer} cardContainer - The cards this view pages through; it builds this view and hands itself in.
   * @param {Tracker} tracker - Logs the paging.
   */
  constructor(root, viewerParams, cardContainer, tracker) {
    this.#root = root;
    this.#viewerParams = viewerParams;
    this.#cardContainer = cardContainer;
    this.#tracker = tracker;
  }

  /**
   * Creates an ExpandedView and initializes its LabelDetail controller.
   * @param {HTMLElement} root - The `.gallery-expanded-view` container element.
   * @param {ViewerParams} viewerParams - What the imagery viewer is built with.
   * @param {CardContainer} cardContainer - The cards this view pages through.
   * @param {Tracker} tracker - Logs the paging.
   * @returns {Promise<ExpandedView>}
   */
  static async create(root, viewerParams, cardContainer, tracker) {
    const expandedView = new ExpandedView(root, viewerParams, cardContainer, tracker);
    await expandedView.#init();
    return expandedView;
  }

  /**
   * Initialization: instantiate LabelDetail inside the host root, then wire up close and paging buttons.
   */
  async #init() {
    const root = this.#root;
    this.open = false;
    this.cardIndex = -1;
    this.refCard = null;
    this.pendingCardIndex = undefined; // Set by nextLabel/previousLabel for cross-page navigation.

    // Initialize the shared LabelDetail controller inside our inline host.
    this.labelDetail = await LabelDetail.create(root, {
      admin: false,
      viewerType: this.#viewerParams.panoViewerType,
      viewerAccessToken: this.#viewerParams.viewerAccessToken,
      currUsername: this.#viewerParams.currUsername,
      onVote: this.#handleVote,
      onEdit: this.#handleEdit,
      onComments: this.#handleComments,
      onDelete: this.#handleDelete,
      panoOverlaySource: 'GalleryExpandedImage',
      voteColumnSource: 'GalleryExpandedThumbs',
      showLabelMapLink: true,
      showExploreHereLink: true,
      // This host hides its panel with CSS rather than closing a <dialog>, so the card can't tell on its own
      // whether it is on screen — and its keyboard shortcuts (#5194) must not vote on the last-shown label after
      // the panel has been closed.
      isOpen: () => this.open,
    });

    // Expose panoManager for Keyboard.js zoom shortcuts.
    this.panoManager = this.labelDetail.panoManager;

    // Wire paging buttons.
    this.leftArrow = root.querySelector('.label-detail__paging--prev');
    this.rightArrow = root.querySelector('.label-detail__paging--next');
    // A click whose `detail` is 0 didn't come from a pointer: it's the card's arrow-key shortcut (#5194), or Enter
    // or Space on the focused arrow. Both stay logged as keyboard shortcuts rather than clicks.
    if (this.leftArrow) this.leftArrow.addEventListener('click', (e) => this.previousLabel(e.detail === 0));
    if (this.rightArrow) this.rightArrow.addEventListener('click', (e) => this.nextLabel(e.detail === 0));

    // Wire close button.
    const closeBtn = root.querySelector('[data-action="close-label-detail"]');
    if (closeBtn) closeBtn.addEventListener('click', () => this.closeExpandedViewAndRemoveCardTransparency());

    // Capture a ?labelId= deep link now: the initial query's refreshUI() closes the expanded view, which also
    // scrubs the param from the URL, so it must be read before that and acted on after (restoreFromUrl()).
    this.initialUrlLabelId = LabelDetail.urlLabelId();
    // That label is what this visit is for, so start its viewer (the billable, deferred part of LabelDetail's
    // init) now rather than after the cards have rendered. The container is visibility:hidden, which keeps its
    // layout, so the viewer can still measure it. Bounded so a provider that never loads can't hold the cards.
    if (this.initialUrlLabelId) await this.panoManager.warmUp(PopupPanoManager.DEEP_LINK_BUILD_WAIT_MS);
  }

  /**
   * Reopens the label a shared or refreshed ?labelId= URL points at. Called by CardContainer after cards have
   * rendered — any earlier and the initial query's refreshUI() would immediately close it again. The deep link
   * opens the detail directly by id: no thumbnail highlight, and paging picks up from the first card.
   */
  restoreFromUrl() {
    if (!this.initialUrlLabelId) return;
    const labelId = this.initialUrlLabelId;
    this.initialUrlLabelId = null; // One shot; later renders (paging, filters) shouldn't reopen it.
    LabelDetail.syncUrlLabelId(labelId); // refreshUI's close scrubbed the param; put it back for refresh/re-share.

    // A review list (#5444) knows where the label sits, so open it by index: prev/next then walk the list from
    // there and the position indicator has something to count. A ?labelId= naming a label outside the list still
    // falls through to the by-id path below.
    if (this.#cardContainer.isListMode() && this.#cardContainer.jumpToLabel(labelId)) return;

    this.#root.style.visibility = 'visible';
    this.open = true;
    // With no reference card, paging picks up from the first card (Next), so there is nothing to page back to;
    // an enabled Prev here would drive cardIndex below -1 and break the paging state machine.
    if (this.leftArrow) this.leftArrow.disabled = true;
    this.labelDetail.showLabel(labelId, 'GalleryExpanded')
      .catch(() => this.closeExpandedViewAndRemoveCardTransparency());
  }

  /**
   * Converts a Gallery Card's properties into the meta object shape that LabelDetail.showLabel() expects.
   * @param {Card} card
   * @returns {object} A meta object compatible with LabelDetail._handleData().
   */
  #cardToMeta(card) {
    const p = card.getProperties();
    return {
      label_id: p.label_id,
      label_type: p.label_type,
      pano_id: p.pano_id,
      lat: p.lat,
      lng: p.lng,
      camera_lat: p.camera_lat,
      camera_lng: p.camera_lng,
      image_capture_date: p.image_capture_date,
      timestamp: p.label_timestamp,
      heading: p.heading,
      pitch: p.pitch,
      zoom: p.zoom,
      canvas_x: p.original_canvas_x,
      canvas_y: p.original_canvas_y,
      canvas_width: p.original_canvas_width,
      canvas_height: p.original_canvas_height,
      pano_source: p.pano_source,
      severity: p.severity,
      description: p.description,
      street_edge_id: p.street_edge_id,
      region_id: p.region_id,
      num_agree: p.val_counts.Agree,
      num_disagree: p.val_counts.Disagree,
      num_unsure: p.val_counts.Unsure,
      user_validation: p.user_validation,
      ai_validation: p.ai_validation,
      tags: p.tags,
      ai_generated: p.ai_generated,
      crop_url: card.getCropUrl(),
      crop_marker: card.getCropMarker(),
      backup_image: card.getBackupImageData(),
      pano_data: p.pano_data,
      from_current_user: p.from_current_user,
      can_edit: p.can_edit,
      deleted: p.deleted,
      can_restore: p.can_restore,
      expired: p.expired,
      comments: p.comments,
    };
  }

  /**
   * Called by LabelDetail after a successful validation POST. Syncs the new vote back onto the small card.
   * @param {'Agree'|'Disagree'|'Unsure'} action
   */
  #handleVote = (action) => {
    if (this.refCard) {
      this.refCard.updateUserValidation(action);
    }
  };

  /**
   * Called by LabelDetail after a successful edit (#2575, #3671); syncs the small card.
   * @param {{label_type: string, severity: ?number, tags: string[]}} meta - The label's metadata as it now stands.
   */
  #handleEdit = (meta) => {
    if (this.refCard) {
      this.refCard.updateLabelType(meta.label_type);
      this.refCard.updateSeverityAndTags(meta.severity, meta.tags);
    }
  };

  /**
   * Syncs the small card's comments so reopening the label shows them. Looked up by id, like #handleDelete.
   * @param {{label_id: number, comments: Array<Record<string, any>|string>}} meta - The label's metadata.
   */
  #handleComments = (meta) => {
    this.#cardContainer.findCardByLabelId(meta.label_id)?.updateComments(meta.comments);
  };

  /**
   * Called by LabelDetail after a delete or restore (#3591); syncs the small card, incl. an admin delete's Disagree.
   * Looked up by id, since paging while the request was in flight may have moved refCard on to a neighbor.
   * @param {{label_id: number, deleted: boolean, can_restore: boolean, user_validation: ?string}} meta - The
   *     label's metadata as it now stands.
   */
  #handleDelete = (meta) => {
    const card = this.#cardContainer.findCardByLabelId(meta.label_id);
    if (!card) return;
    card.setDeleted(!!meta.deleted, !!meta.can_restore);
    // An admin's delete files a Disagree but leaves their comment; a restore clears that vote the same way.
    card.updateUserValidation(meta.user_validation ?? null, { dropOwnComment: false });
  };

  /**
   * Opens the expanded view for the current refCard by passing the card data to LabelDetail.
   */
  #openExpandedView() {
    // A grid card's pop-up would otherwise float over the view opening on top of its card.
    closeReasonPopover();
    // Clear the close-guard so the in-flight pano load is allowed to show once this card's load completes.
    const panoEl = this.#root.querySelector('.label-detail__pano');
    if (panoEl) delete panoEl.dataset.closedDuringLoad;

    const meta = this.#cardToMeta(this.refCard);
    this.labelDetail.showLabel(meta, 'GalleryExpanded');

    // Highlight selected card thumbnail.
    this.#highlightThumbnail(document.getElementById(`gallery_card_${this.refCard.getLabelId()}`));
    this.#root.style.visibility = 'visible';
    this.open = true;
    LabelDetail.syncUrlLabelId(this.refCard.getLabelId());
  }

  /**
   * Performs the actions to close the expanded view.
   * NOTE: does not remove card transparency. For that, use closeExpandedViewAndRemoveCardTransparency().
   */
  closeExpandedView() {
    // Hiding by visibility leaves the pop-up's host connected, so it wouldn't notice; one holding an "Other…" draft
    // even outlasts the click on ✕.
    closeReasonPopover();
    this.#root.style.visibility = 'hidden';
    LabelDetail.syncUrlLabelId(null);
    // Clear the inline visibility set by PopupPanoManager.setPano() so the parent's visibility:hidden cascades.
    // Also set a data flag so that if a pano load is still in-flight, it won't reveal itself when it finishes.
    const panoEl = this.#root.querySelector('.label-detail__pano');
    if (panoEl) {
      panoEl.style.visibility = '';
      panoEl.dataset.closedDuringLoad = 'true';
    }
    this.open = false;
  }

  /**
   * Removes transparency from the current page of cards.
   */
  #removeCardTransparency() {
    const currentPageCards = this.#cardContainer.getCurrentPageCards();
    for (const card of currentPageCards) {
      const cardDomEl = document.getElementById(`gallery_card_${card.getLabelId()}`);
      if (cardDomEl && cardDomEl.classList.contains(ExpandedView.#unselectedCardClassName)) {
        cardDomEl.classList.remove(ExpandedView.#unselectedCardClassName);
      }
    }
  }

  /**
   * Closes expanded view, removes transparency from cards, and scrolls the reference card to the top of the viewport.
   */
  closeExpandedViewAndRemoveCardTransparency() {
    const cardToScrollTo = this.refCard;
    this.closeExpandedView();
    this.#removeCardTransparency();
    if (cardToScrollTo) {
      requestAnimationFrame(() => {
        const cardEl = document.getElementById(`gallery_card_${cardToScrollTo.getLabelId()}`);
        if (cardEl) {
          const navbar = document.getElementById('header');
          const navbarHeight = navbar ? navbar.offsetHeight : 0;
          // The grid's row gap is the card's breathing room, so leaving it above the card stops it landing flush
          // against the navbar.
          const gridRowGap = parseFloat(window.getComputedStyle(cardEl.parentElement).rowGap) || 0;
          const targetScrollY = cardEl.getBoundingClientRect().top + window.scrollY - navbarHeight - gridRowGap;
          window.scrollTo({ top: targetScrollY, behavior: 'smooth' });
        }
      });
    }
  }

  /**
   * Tries to update the current card to the given input index.
   * @param {number} index - The index of the card to update to.
   */
  #updateExpandedViewCardByIndex(index) {
    if (this.leftArrow) this.leftArrow.disabled = false;
    if (this.rightArrow) this.rightArrow.disabled = false;
    this.cardIndex = index;
    this.refCard = this.#cardContainer.getCardByIndex(this.cardIndex);

    // An index with no card behind it (e.g. paging from a deep link on an empty page, or a filter change that
    // shrank the card set) has nothing to show — close gracefully rather than crash on the missing card.
    if (!this.refCard) {
      this.closeExpandedViewAndRemoveCardTransparency();
      return;
    }

    this.#openExpandedView();
    this.#updatePosition(index);

    if (this.cardIndex === 0 && this.leftArrow) this.leftArrow.disabled = true;

    if (this.#cardContainer.isLastPage()) {
      const page = this.#cardContainer.getCurrentPage();
      const lastCardIndex
        = (page - 1) * this.#cardContainer.getCardsPerPage() + this.#cardContainer.getCurrentPageCards().length - 1;
      if (this.cardIndex === lastCardIndex && this.rightArrow) this.rightArrow.disabled = true;
    }
  }

  /**
   * Shows how far through a review list the current card is ("k of N"), or hides the indicator outside list mode.
   *
   * Only a `?labelIds=` list has an N: everywhere else the card set grows as the user pages, so a total would be
   * whatever happened to be loaded rather than how much there is to review.
   *
   * @param {number} index - The current card's index in the list.
   */
  #updatePosition(index) {
    const positionEl = this.#root.querySelector('.label-detail__position');
    if (!positionEl) return;

    const total = this.#cardContainer.isListMode() ? this.#cardContainer.getListSize() : 0;
    positionEl.hidden = total === 0;
    positionEl.textContent = total === 0 ? '' : i18next.t('gallery:list-position', { k: index + 1, n: total });
  }

  /**
   * Opens the view on the card at this index, or closes it when no card is there.
   * @param {number} newIndex - The new index of the card being displayed.
   */
  updateCardIndex(newIndex) {
    this.#updateExpandedViewCardByIndex(newIndex);
  }

  /**
   * Moves to the next label.
   * @param {boolean} keyboardShortcut - Whether the action came from a keyboard shortcut.
   */
  nextLabel(keyboardShortcut) {
    this.#tracker.push(`NextLabel${keyboardShortcut ? 'KeyboardShortcut' : 'Click'}`);
    // Page size is asked of the container on every use rather than copied into this class: it differs between the
    // filtered grid and a review list (#5444), and a stale copy would page past or repeat a label at the boundary.
    const page = this.#cardContainer.getCurrentPage();
    if (this.cardIndex < page * this.#cardContainer.getCardsPerPage() - 1) {
      this.#updateExpandedViewCardByIndex(this.cardIndex + 1);
    } else {
      this.cardIndex += 1;
      this.pendingCardIndex = this.cardIndex;
      this.#cardContainer.nextPage();
    }
  }

  /**
   * Moves to the previous label.
   * @param {boolean} keyboardShortcut - Whether the action came from a keyboard shortcut.
   */
  previousLabel(keyboardShortcut) {
    this.#tracker.push(`PrevLabel${keyboardShortcut ? 'KeyboardShortcut' : 'Click'}`);
    const page = this.#cardContainer.getCurrentPage();
    if (this.cardIndex > (page - 1) * this.#cardContainer.getCardsPerPage()) {
      this.#updateExpandedViewCardByIndex(this.cardIndex - 1);
    } else {
      this.cardIndex -= 1;
      this.pendingCardIndex = this.cardIndex;
      this.#cardContainer.prevPage();
    }
  }

  /**
   * Highlights the selected thumbnail and dims the rest of the cards on the page.
   * @param {HTMLElement} galleryCard
   */
  #highlightThumbnail(galleryCard) {
    // Scroll the selected card into view.
    const index = this.cardIndex;
    const page = this.#cardContainer.getCurrentPage();
    const totalCards = this.#cardContainer.getCurrentCards().getSize();
    galleryCard.scrollIntoView({
      block: (index < page * this.#cardContainer.getCardsPerPage() - 1 && index < totalCards - 1) ? 'center' : 'end',
      behavior: 'smooth',
    });

    // Remove transparent effect from selected card.
    galleryCard.classList.remove(ExpandedView.#unselectedCardClassName);

    // The rest of the cards should be semitransparent.
    const currentPageCards = this.#cardContainer.getCurrentPageCards();
    for (const card of currentPageCards) {
      const cardLabelId = card.getLabelId();
      if (cardLabelId !== this.refCard.getLabelId()) {
        const cardDomEl = document.getElementById(`gallery_card_${cardLabelId}`);
        if (cardDomEl && !cardDomEl.classList.contains(ExpandedView.#unselectedCardClassName)) {
          cardDomEl.classList.add(ExpandedView.#unselectedCardClassName);
        }
      }
    }
  }

  /**
   * Returns the reference card currently being displayed.
   * @returns {Card}
   */
  getReferenceCard() {
    return this.refCard;
  }

  /**
   * Called by CardContainer after new cards have been rendered to the DOM. If a cross-page navigation
   * was pending (i.e., the user clicked next/prev from the last/first card on a page), reopens the
   * expanded view for the target card on the new page.
   */
  onPageCardsRendered() {
    if (this.pendingCardIndex === undefined) return;
    const idx = this.pendingCardIndex;
    this.pendingCardIndex = undefined;
    this.#updateExpandedViewCardByIndex(idx);
  }
}
